<template>
  <div class="container-fluid px-4 py-4 mb-5" style="background:#f8fafc;min-height:100vh;">

    <!-- Access gate -->
    <div v-if="!isDukakitUser" class="text-center py-5">
      <i class="bi bi-shield-lock text-danger" style="font-size:3rem;"></i>
      <h5 class="mt-3">Access Denied</h5>
      <p class="text-secondary">This dashboard is restricted to Dukakit internal team members.</p>
    </div>

    <template v-else>
      <!-- ---------------------------------------------------------------- -->
      <!-- Header -->
      <!-- ---------------------------------------------------------------- -->
      <div
        class="d-flex align-items-center justify-content-between flex-wrap pb-3 mb-4"
        style="gap:12px;border-bottom:1px solid #e9ecef;"
      >
        <div>
          <h4 class="mb-0 text-primary" style="font-size:21px;font-weight:600;letter-spacing:-0.3px;">Dukakit Internal</h4>
          <small class="text-secondary">Operations Dashboard</small>
        </div>

        <div class="d-flex align-items-center flex-wrap" style="gap:8px;">
          <!-- Global Search -->
          <div class="position-relative" v-click-outside="closeSearch">
            <div class="pill-input" style="width:280px;">
              <i class="bi bi-search text-secondary"></i>
              <input
                v-model="globalSearch.query"
                @input="onGlobalSearchInput"
                @focus="globalSearch.open = true"
                @keydown.esc="closeSearch"
                @keydown.enter="globalSearch.results[0] && selectSearchResult(globalSearch.results[0])"
                placeholder="Search merchant, email, phone…"
                autocomplete="off"
              />
              <i v-if="globalSearch.query" class="bi bi-x-lg text-secondary" style="cursor:pointer;" @click="globalSearch.query = ''; closeSearch()"></i>
            </div>
            <!-- Results dropdown -->
            <div
              v-if="globalSearch.open && (globalSearch.results.length > 0 || globalSearch.loading)"
              class="position-absolute bg-white border w-100"
              style="top:calc(100% + 4px);left:0;z-index:1060;max-height:280px;overflow-y:auto;border-radius:12px;border-color:#e9ecef !important;"
            >
              <div v-if="globalSearch.loading" class="text-center py-3">
                <div class="spinner-border spinner-border-sm text-secondary"></div>
              </div>
              <template v-else>
                <a
                  v-for="result in globalSearch.results"
                  :key="result.id"
                  class="d-flex align-items-center px-3 py-2 border-bottom search-result-item"
                  @click="selectSearchResult(result)"
                  style="cursor:pointer;text-decoration:none;color:inherit;"
                >
                  <div
                    class="rounded-circle d-flex align-items-center justify-content-center text-white font-weight-bold mr-3 flex-shrink-0"
                    :style="{ background: searchAvatarColor(result.name), width:'32px', height:'32px', fontSize:'.7rem' }"
                  >
                    {{ searchInitials(result.name) }}
                  </div>
                  <div class="flex-grow-1 min-width-0">
                    <div class="font-weight-bold text-truncate" style="font-size:.85rem;">{{ result.name }}</div>
                    <small class="text-secondary">{{ result.email }}</small>
                  </div>
                  <span :class="['pill ml-2', searchStatusBadge(result)]" style="flex-shrink:0;">
                    {{ searchStatusLabel(result) }}
                  </span>
                </a>
                <div v-if="globalSearch.results.length === 0" class="text-center text-secondary py-3">
                  <small>No results</small>
                </div>
              </template>
            </div>
          </div>

          <date-range-picker
            :auto-apply="true"
            v-model="dateRange"
            class="header-date-picker"
            :ranges="false"
          >
            <template #input="picker">
              <span class="pill-input" style="cursor:pointer;">
                <i class="bi bi-calendar-event text-secondary"></i>
                <template v-if="picker.startDate">
                  {{ formatDateShort(dateRange.startDate) }} — {{ formatDateShort(picker.endDate) }}
                </template>
                <span v-else class="text-secondary">Filter by date</span>
              </span>
            </template>
          </date-range-picker>

          <button
            class="btn btn-dark d-inline-flex align-items-center"
            style="gap:6px;border-radius:8px;font-size:13px;"
            @click="refreshAll"
            :disabled="refreshing"
          >
            <i class="bi bi-arrow-clockwise" :class="{ 'anim-spin': refreshing }"></i>
            {{ refreshing ? 'Refreshing…' : 'Refresh' }}
          </button>
        </div>
      </div>

      <!-- ---------------------------------------------------------------- -->
      <!-- Smart Alerts -->
      <!-- ---------------------------------------------------------------- -->
      <admin-alerts-banner ref="alertsBanner" />

      <!-- ---------------------------------------------------------------- -->
      <!-- KPI Cards -->
      <!-- ---------------------------------------------------------------- -->
      <admin-kpi-cards :kpis="kpis" :loading="kpiLoading" :can-view-revenue="can('dukakit.view-revenue')" />

      <!-- ---------------------------------------------------------------- -->
      <!-- Tabs -->
      <!-- ---------------------------------------------------------------- -->
      <div class="pill-tabs mt-4">
        <a
          class="pill-tab"
          :class="{ active: activeTab === 'overview' }"
          @click="activeTab = 'overview'"
        >
          <i class="bi bi-grid-1x2-fill mr-1"></i> Overview
        </a>
        <a
          class="pill-tab"
          :class="{ active: activeTab === 'companies' }"
          @click="activeTab = 'companies'"
        >
          <i class="bi bi-buildings mr-1"></i> Companies
        </a>
        <a
          v-if="can('dukakit.view-revenue')"
          class="pill-tab"
          :class="{ active: activeTab === 'revenue' }"
          @click="activeTab = 'revenue'"
        >
          <i class="bi bi-currency-dollar mr-1"></i> Revenue
        </a>
        <a
          v-if="can('dukakit.view-system-health')"
          class="pill-tab"
          :class="{ active: activeTab === 'health' }"
          @click="activeTab = 'health'"
        >
          <i class="bi bi-heart-pulse mr-1"></i> System Health
        </a>
        <a
          v-if="can('dukakit.manage-announcements')"
          class="pill-tab"
          :class="{ active: activeTab === 'announcements' }"
          @click="activeTab = 'announcements'"
        >
          <i class="bi bi-megaphone mr-1"></i> Announcements
        </a>
        <a
          v-if="can('dukakit.manage-announcements')"
          class="pill-tab"
          :class="{ active: activeTab === 'blast' }"
          @click="activeTab = 'blast'"
        >
          <i class="bi bi-send-fill mr-1"></i> Blast / SMS
        </a>
        <a
          v-if="can('dukakit.manage-coupons')"
          class="pill-tab"
          :class="{ active: activeTab === 'coupons' }"
          @click="activeTab = 'coupons'"
        >
          <i class="bi bi-ticket-perforated mr-1"></i> Coupons
        </a>
        <a
          v-if="can('dukakit.view-analytics')"
          class="pill-tab"
          :class="{ active: activeTab === 'analytics' }"
          @click="activeTab = 'analytics'"
        >
          <i class="bi bi-bar-chart-line mr-1"></i> Analytics
        </a>
        <a
          v-if="can('dukakit.view-audit-log')"
          class="pill-tab"
          :class="{ active: activeTab === 'audit' }"
          @click="activeTab = 'audit'"
        >
          <i class="bi bi-journal-text mr-1"></i> Audit Log
        </a>
        <a
          v-if="can('dukakit.manage-feature-flags')"
          class="pill-tab"
          :class="{ active: activeTab === 'flags' }"
          @click="activeTab = 'flags'"
        >
          <i class="bi bi-toggles mr-1"></i> Feature Flags
        </a>
        <a
          v-if="can('dukakit.manage-staff')"
          class="pill-tab"
          :class="{ active: activeTab === 'staff' }"
          @click="activeTab = 'staff'"
        >
          <i class="bi bi-people-fill mr-1"></i> Staff
        </a>
        <a
          v-if="can('dukakit.manage-product-library')"
          class="pill-tab"
          :class="{ active: activeTab === 'productLibrary' }"
          @click="activeTab = 'productLibrary'"
        >
          <i class="bi bi-box-seam mr-1"></i> Product Library
        </a>
      </div>

      <div class="tab-pane-wrapper mb-4">

        <!-- Overview -->
        <div v-if="activeTab === 'overview'">
          <admin-charts-section
            v-if="can('dukakit.view-analytics')"
            ref="chartsSection"
            :plan-distribution="planDistribution"
          />

          <admin-merchants-panel
            ref="merchantsPanel"
            :can-view-revenue="can('dukakit.view-revenue')"
          />

          <admin-churn-risk
            v-if="can('dukakit.view-analytics')"
            @view-company="openCompanyFromChurn"
          />

          <div class="row mt-2">
            <div class="col-md-7 mb-3">
              <admin-activity-feed ref="activityFeed" />
            </div>
            <div class="col-md-5 mb-3">
              <admin-churn-panel :risks="churnRisks" />
            </div>
          </div>

          <!-- Online users -->
          <admin-online-users-table
            class="mt-2"
            :users="onlineUsers"
            @select="selectedUser = $event"
          />

          <admin-user-detail-modal
            :user="selectedUser"
            @close="selectedUser = null"
            @view-company="openDrawer"
          />
        </div>

        <!-- Companies -->
        <div v-if="activeTab === 'companies'">
          <admin-companies-table
            ref="companiesTable"
            :date-range="dateRange"
            @company-selected="openDrawer"
            @impersonate="impersonateCompany"
            @billing-date="openBillingDateModal"
            @change-plan="openPlanModal"
            @assign-affiliate="openAffiliateModal"
            @suspend="confirmSuspend"
            @delete-inventory="confirmDeleteInventory"
            @mark-test="markAsTest"
            @extend-trial="openExtendTrialModal"
            @waive-overdue="confirmWaiveOverdue"
            @convert-trial="confirmConvertTrial"
            @reactivate="confirmReactivate"
            @archive="confirmArchive"
            @delete-company="confirmDeleteCompany"
            @force-logout="confirmForceLogout"
            @password-reset="confirmPasswordReset"
            @notify="openNotifyModal"
            @copied="onEmailCopied"
          />
        </div>

        <!-- Revenue -->
        <div v-if="activeTab === 'revenue' && can('dukakit.view-revenue')">
          <admin-revenue-tab ref="revenueTab" />
          <admin-cohort-grid v-if="can('dukakit.view-analytics')" />
        </div>

        <!-- System Health -->
        <div v-if="activeTab === 'health' && can('dukakit.view-system-health')">
          <admin-system-health ref="systemHealth" />
        </div>

        <!-- Announcements -->
        <div v-if="activeTab === 'announcements' && can('dukakit.manage-announcements')">
          <admin-announcements />
        </div>

        <!-- Blast Notifications -->
        <div v-if="activeTab === 'blast' && can('dukakit.manage-announcements')">
          <admin-blast-notification />
        </div>

        <!-- Coupons -->
        <div v-if="activeTab === 'coupons' && can('dukakit.manage-coupons')">
          <admin-coupons />
        </div>

        <!-- Analytics -->
        <div v-if="activeTab === 'analytics' && can('dukakit.view-analytics')">
          <admin-event-analytics />
        </div>

        <!-- Audit Log -->
        <div v-if="activeTab === 'audit' && can('dukakit.view-audit-log')">
          <admin-audit-log />
        </div>

        <!-- Staff -->
        <div v-if="activeTab === 'staff' && can('dukakit.manage-staff')">
          <admin-staff-manager />
        </div>

        <!-- Product Library -->
        <div v-if="activeTab === 'productLibrary' && can('dukakit.manage-product-library')">
          <product-library-builder />
        </div>

        <!-- Feature Flags -->
        <div v-if="activeTab === 'flags' && can('dukakit.manage-feature-flags')">
          <admin-feature-flags />
        </div>
      </div>

      <!-- ---------------------------------------------------------------- -->
      <!-- Company Drawer -->
      <!-- ---------------------------------------------------------------- -->
      <admin-company-drawer
        :company="selectedCompany"
        :visible="drawerVisible"
        @close="drawerVisible = false"
        @impersonate="impersonateCompany"
        @billing-date="openBillingDateModal"
        @change-plan="openPlanModal"
        @assign-affiliate="openAffiliateModal"
        @suspend="confirmSuspend"
        @delete-inventory="confirmDeleteInventory"
        @mark-test="markAsTest"
        @extend-trial="openExtendTrialModal"
        @waive-overdue="confirmWaiveOverdue"
        @convert-trial="confirmConvertTrial"
        @reactivate="confirmReactivate"
        @archive="confirmArchive"
        @delete-company="confirmDeleteCompany"
        @force-logout="confirmForceLogout"
        @password-reset="confirmPasswordReset"
        @notify="openNotifyModal"
      />

      <!-- ---------------------------------------------------------------- -->
      <!-- Billing Date Modal -->
      <!-- ---------------------------------------------------------------- -->
      <b-modal ref="billingDateModal" hide-footer title="Adjust Billing Date">
        <form @submit.prevent="submitBillingDate" class="text-center">
          <date-range-picker
            :auto-apply="true"
            v-model="billingDateForm.newDate"
            class="custom-width-datepicker"
            :single-date-picker="true"
            :ranges="false"
          >
            <template #input="picker">
              <p v-if="billingDateForm.newDate.startDate">
                {{ formatDateShort(billingDateForm.newDate.startDate) }}
              </p>
              <p v-else class="text-muted"><i class="bi bi-calendar-event"></i> Select Date</p>
            </template>
          </date-range-picker>
          <button class="btn btn-primary btn-block mt-3" :disabled="billingDateForm.saving">
            <span v-if="billingDateForm.saving" class="spinner-border spinner-border-sm mr-1"></span>
            Save
          </button>
        </form>
      </b-modal>

      <!-- ---------------------------------------------------------------- -->
      <!-- Change Plan Modal -->
      <!-- ---------------------------------------------------------------- -->
      <b-modal ref="planModal" hide-footer title="Change Subscription Plan">
        <form @submit.prevent="submitPlanChange">

          <!-- Standard plan selector -->
          <div v-if="!planForm.isCustom" class="form-group">
            <label class="font-weight-bold">Plan</label>
            <select v-model="planForm.plan" class="form-control">
              <option value="">Select plan</option>
              <option v-for="plan in subscriptionPlans" :key="plan.id" :value="plan.id">
                {{ plan.name }} — KES {{ Number(plan.amount).toLocaleString() }}
              </option>
            </select>
          </div>

          <!-- Custom / negotiated rate toggle -->
          <div class="form-group">
            <div class="custom-control custom-switch">
              <input v-model="planForm.isCustom" type="checkbox" class="custom-control-input" id="customRateSwitch">
              <label class="custom-control-label" for="customRateSwitch">Custom / Negotiated Rate</label>
            </div>
          </div>

          <!-- Custom amount input (only when toggled) -->
          <div v-if="planForm.isCustom" class="form-group">
            <label class="font-weight-bold">Custom Amount <small class="text-muted">(KES / month)</small></label>
            <input
              v-model.number="planForm.customAmount"
              type="number"
              min="1"
              class="form-control"
              placeholder="e.g. 2000"
              required
            />
          </div>

          <!-- Internal notes (always visible) -->
          <div class="form-group">
            <label class="font-weight-bold">Billing Notes <small class="text-muted">(internal)</small></label>
            <input
              v-model="planForm.billingNotes"
              type="text"
              class="form-control"
              placeholder="e.g. Negotiated enterprise rate, 6-month contract"
            />
          </div>

          <button
            class="btn btn-primary btn-block"
            type="submit"
            :disabled="planForm.saving || (planForm.isCustom ? !planForm.customAmount : !planForm.plan)"
          >
            <span v-if="planForm.saving" class="spinner-border spinner-border-sm mr-1"></span>
            Update Plan
          </button>
        </form>
      </b-modal>

      <!-- ---------------------------------------------------------------- -->
      <!-- Assign Affiliate Modal -->
      <!-- ---------------------------------------------------------------- -->
      <b-modal ref="affiliateModal" hide-footer title="Assign to Affiliate">
        <div class="text-center">
          <h6 v-if="actionTarget">{{ actionTarget.name }}</h6>
          <div class="form-group">
            <input
              v-model="affiliateForm.affiliateCode"
              class="form-control"
              placeholder="Type affiliate name…"
            />
            <div v-if="filteredAffiliates.length > 0 && affiliateForm.affiliateCode" class="list-group mt-1 text-left" style="max-height:150px;overflow-y:auto;">
              <a
                v-for="aff in filteredAffiliates"
                :key="aff.id"
                class="list-group-item list-group-item-action py-1"
                @click="selectAffiliate(aff)"
                style="cursor:pointer;"
              >
                {{ aff.affiliate_code }}
              </a>
            </div>
          </div>
          <select v-model="affiliateForm.package" class="form-control mb-3">
            <option value="">Select package</option>
            <option value="in-house">Subscription only (In-house)</option>
            <option value="3000">KES 3,000</option>
            <option value="5000">KES 5,000</option>
            <option value="10000">KES 10,000</option>
          </select>
          <button
            class="btn btn-primary btn-block"
            @click="submitAffiliateAssign"
            :disabled="!affiliateForm.selectedAffiliate || !affiliateForm.package || affiliateForm.saving"
          >
            <span v-if="affiliateForm.saving" class="spinner-border spinner-border-sm mr-1"></span>
            Assign
          </button>
        </div>
      </b-modal>

      <!-- ---------------------------------------------------------------- -->
      <!-- Extend Trial Modal -->
      <!-- ---------------------------------------------------------------- -->
      <b-modal ref="extendTrialModal" hide-footer title="Extend Trial / Billing">
        <div class="text-center">
          <p class="mb-3 text-muted" v-if="actionTarget">
            Extending billing for <strong>{{ actionTarget.name }}</strong>
          </p>
          <div class="input-group mb-3">
            <input
              v-model.number="extendTrialForm.days"
              type="number"
              min="1"
              max="365"
              class="form-control text-center"
              placeholder="Days"
            />
            <div class="input-group-append">
              <span class="input-group-text">days</span>
            </div>
          </div>
          <div class="btn-group mb-3">
            <button class="btn btn-outline-secondary" @click="extendTrialForm.days = 7">7d</button>
            <button class="btn btn-outline-secondary" @click="extendTrialForm.days = 14">14d</button>
            <button class="btn btn-outline-secondary" @click="extendTrialForm.days = 30">30d</button>
            <button class="btn btn-outline-secondary" @click="extendTrialForm.days = 90">90d</button>
          </div>
          <button
            class="btn btn-primary btn-block"
            :disabled="!extendTrialForm.days || extendTrialForm.saving"
            @click="submitExtendTrial"
          >
            <span v-if="extendTrialForm.saving" class="spinner-border spinner-border-sm mr-1"></span>
            Extend
          </button>
        </div>
      </b-modal>

      <!-- ---------------------------------------------------------------- -->
      <!-- Send Notification Modal -->
      <!-- ---------------------------------------------------------------- -->
      <b-modal ref="notifyModal" hide-footer title="Send In-App Notification">
        <div v-if="actionTarget">
          <p class="text-muted mb-3">Send a message to all users at <strong>{{ actionTarget.name }}</strong>.</p>
          <div class="form-group">
            <label class="font-weight-bold">Title</label>
            <input v-model="notifyForm.title" class="form-control" placeholder="Message from Dukakit" />
          </div>
          <div class="form-group">
            <label class="font-weight-bold">Message <span class="text-danger">*</span></label>
            <textarea
              v-model="notifyForm.message"
              class="form-control"
              rows="4"
              placeholder="Your message…"
              maxlength="500"
            ></textarea>
            <small class="text-muted">{{ notifyForm.message.length }}/500</small>
          </div>
          <button
            class="btn btn-primary btn-block mt-2"
            :disabled="!notifyForm.message.trim() || notifyForm.saving"
            @click="submitNotify"
          >
            <span v-if="notifyForm.saving" class="spinner-border spinner-border-sm mr-1"></span>
            Send Notification
          </button>
        </div>
      </b-modal>

    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { dukakitCan } from '../../utils/dukakitCan';
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker';
import AdminKpiCards from './AdminKpiCards.vue';
import AdminChartsSection from './AdminChartsSection.vue';
import AdminChurnPanel from './AdminChurnPanel.vue';
import AdminActivityFeed from './AdminActivityFeed.vue';
import AdminCompaniesTable from './AdminCompaniesTable.vue';
import AdminCompanyDrawer from './AdminCompanyDrawer.vue';
import AdminSystemHealth from './AdminSystemHealth.vue';
import AdminRevenueTab from './AdminRevenueTab.vue';
import AdminStaffManager from './AdminStaffManager.vue';
import AdminAlertsBanner from './AdminAlertsBanner.vue';
import clearCompanyScopedCache from '../../services/companyCache';
import AdminMerchantsPanel from './AdminMerchantsPanel.vue';
import AdminFeatureFlags from './AdminFeatureFlags.vue';
import AdminAuditLog from './AdminAuditLog.vue';
import AdminAnnouncements from './AdminAnnouncements.vue';
import AdminBlastNotification from './AdminBlastNotification.vue';
import AdminCoupons from './AdminCoupons.vue';
import AdminChurnRisk from './AdminChurnRisk.vue';
import AdminCohortGrid from './AdminCohortGrid.vue';
import AdminEventAnalytics from './AdminEventAnalytics.vue';
import ProductLibraryBuilder from './ProductLibraryBuilder.vue';
import AdminOnlineUsersTable from './AdminOnlineUsersTable.vue';
import AdminUserDetailModal from './AdminUserDetailModal.vue';

export default {
  name: 'AdminDashboard',

  directives: {
    'click-outside': {
      bind(el, binding) {
        el._clickOutside = (e) => { if (!el.contains(e.target)) binding.value(e); };
        document.addEventListener('click', el._clickOutside);
      },
      unbind(el) {
        document.removeEventListener('click', el._clickOutside);
      },
    },
  },

  components: {
    DateRangePicker,
    AdminKpiCards,
    AdminChartsSection,
    AdminChurnPanel,
    AdminActivityFeed,
    AdminCompaniesTable,
    AdminCompanyDrawer,
    AdminSystemHealth,
    AdminRevenueTab,
    AdminStaffManager,
    AdminAlertsBanner,
    AdminMerchantsPanel,
    AdminFeatureFlags,
    AdminAuditLog,
    AdminAnnouncements,
    AdminBlastNotification,
    AdminCoupons,
    AdminChurnRisk,
    AdminCohortGrid,
    AdminEventAnalytics,
    ProductLibraryBuilder,
    AdminOnlineUsersTable,
    AdminUserDetailModal,
  },

  data() {
    return {
      activeTab: 'overview',
      kpiLoading: false,
      refreshing: false,

      kpis: {},
      planDistribution: [],
      churnRisks: [],
      onlineUsers: [],

      dateRange: { startDate: null, endDate: null },

      selectedCompany: null,
      drawerVisible: false,
      actionTarget: null,
      selectedUser: null,

      subscriptionPlans: [],

      billingDateForm: {
        newDate: { startDate: '' },
        saving: false,
      },

      planForm: {
        plan:          '',
        isCustom:      false,
        customAmount:  null,
        billingNotes:  '',
        saving:        false,
      },

      affiliateForm: {
        affiliateCode: '',
        selectedAffiliate: null,
        package: '',
        saving: false,
      },
      affiliates: [],

      globalSearch: {
        query: '',
        results: [],
        loading: false,
        open: false,
        debounceTimer: null,
      },

      extendTrialForm: {
        days: 30,
        saving: false,
      },

      notifyForm: {
        title: '',
        message: '',
        saving: false,
      },
    };
  },

  computed: {
    ...mapGetters(['userDetails']),

    isDukakitUser() {
        // console.log(this.userDetails,this.userDetails.isDukakit)
      return this.userDetails && this.userDetails.isDukakit === 1;
    },

    filteredAffiliates() {
      if (!this.affiliateForm.affiliateCode || !Array.isArray(this.affiliates)) return [];
      const term = this.affiliateForm.affiliateCode.toLowerCase();
      return this.affiliates.filter(a => a.affiliate_code && a.affiliate_code.toLowerCase().includes(term));
    },
  },

  mounted() {
    if (this.isDukakitUser) {
      this.fetchDashboard();
      this.fetchOnlineUsers();
    }
  },

  methods: {
    can(permission) {
      return dukakitCan(this.userDetails, permission);
    },

    async fetchDashboard() {
      this.kpiLoading = true;
      try {
        const { data } = await axios.get('/api/admin/dashboard');
        this.kpis             = data.kpis;
        this.planDistribution = data.plan_distribution;
        this.churnRisks       = data.churn_risks;
      } finally {
        this.kpiLoading = false;
      }
    },

    async fetchOnlineUsers() {
      try {
        const { data } = await axios.get('/api/super/all-users');
        this.onlineUsers = data.active || [];
      } catch (e) { /* silent */ }
    },

    async refreshAll() {
      this.refreshing = true;
      await Promise.all([
        this.fetchDashboard(),
        this.fetchOnlineUsers(),
      ]);
      if (this.$refs.chartsSection) this.$refs.chartsSection.refresh();
      if (this.$refs.companiesTable) this.$refs.companiesTable.refresh();
      if (this.$refs.merchantsPanel) this.$refs.merchantsPanel.refresh();
      this.refreshing = false;
    },

    // -----------------------------------------------------------------------
    // Drawer
    // -----------------------------------------------------------------------
    openDrawer(company) {
      this.selectedCompany = company;
      this.drawerVisible   = true;
    },

    // -----------------------------------------------------------------------
    // Actions
    // -----------------------------------------------------------------------
    impersonateCompany(company) {
      axios.get('/api/switch-branch?spadmin=1', { params: { companyId: company.id } })
        .then(r => {
          if (r.status === 200) {
            // Every one of these must clear on a company switch — same as
            // branchSwitch.switchTo() — this call bypasses that shared
            // helper (different endpoint flag, different redirect target)
            // so it has to repeat the clearing itself.
            this.$store.commit('resetStore');
            this.$store.commit('removeUserDetails');
            clearCompanyScopedCache();
            window.location.replace('/dashboard');
          }
        });
    },

    openBillingDateModal(company) {
      this.actionTarget                = company;
      this.billingDateForm.newDate     = { startDate: '' };
      this.$refs.billingDateModal.show();
    },

    async submitBillingDate() {
      if (!this.billingDateForm.newDate.startDate) return;
      this.billingDateForm.saving = true;
      try {
        await axios.post('/api/update-billing-due-date', {
          newDate:   this.billingDateForm.newDate.startDate,
          companyId: this.actionTarget.id,
        });
        this.$refs.billingDateModal.hide();
        this.$toasts.push({ message: 'Billing date updated', type: 'success' });
        this.fetchDashboard();
      } finally {
        this.billingDateForm.saving = false;
      }
    },

    async openPlanModal(company) {
      this.actionTarget         = company;
      this.planForm.plan        = company.billing?.subscription_plan_id ?? '';
      this.planForm.isCustom    = company.billing?.is_custom ?? false;
      this.planForm.customAmount = company.billing?.custom_amount ?? null;
      this.planForm.billingNotes = company.billing?.billing_notes ?? '';
      if (this.subscriptionPlans.length === 0) {
        const { data } = await axios.get('/api/all-subscription-plan');
        this.subscriptionPlans = data;
      }
      this.$refs.planModal.show();
    },

    async submitPlanChange() {
      this.planForm.saving = true;
      try {
        await axios.post('/api/update-subscription-plan', {
          companyId:     this.actionTarget.id,
          plan:          this.planForm.plan,
          is_custom:     this.planForm.isCustom,
          custom_amount: this.planForm.customAmount,
          billing_notes: this.planForm.billingNotes,
        });
        this.$refs.planModal.hide();
        this.$toasts.push({ message: 'Subscription plan updated', type: 'success' });
        this.fetchDashboard();
      } finally {
        this.planForm.saving = false;
      }
    },

    async openAffiliateModal(company) {
      this.actionTarget                    = company;
      this.affiliateForm.affiliateCode     = '';
      this.affiliateForm.selectedAffiliate = null;
      this.affiliateForm.package           = '';
      if (this.affiliates.length === 0) {
        const { data } = await axios.get('/api/affiliates');
        this.affiliates = data;
      }
      this.$refs.affiliateModal.show();
    },

    selectAffiliate(aff) {
      this.affiliateForm.selectedAffiliate = aff;
      this.affiliateForm.affiliateCode     = aff.affiliate_code;
    },

    async submitAffiliateAssign() {
      this.affiliateForm.saving = true;
      try {
        const { data } = await axios.post('/api/assign-to-affiliate', {
          company:   this.actionTarget,
          affiliate: this.affiliateForm.selectedAffiliate,
          package:   this.affiliateForm.package,
        });
        this.$toasts.push({ message: data.message, type: data.type });
        if (data.type === 'success') this.$refs.affiliateModal.hide();
      } finally {
        this.affiliateForm.saving = false;
      }
    },

    confirmSuspend(company) {
      this.$bvModal.msgBoxConfirm(`Suspend ${company.name}? This will delete related billing, orders, products and users.`, {
        title: 'Confirm Suspend',
        okVariant: 'danger',
        okTitle: 'Suspend',
        centered: true,
      }).then(confirmed => {
        if (confirmed) this.suspendCompany(company);
      });
    },

    async suspendCompany(company) {
      await axios.delete('/api/super/delete-company', { params: { id: company.id } });
      this.$toasts.push({ message: `${company.name} suspended`, type: 'success' });
      this.drawerVisible = false;
      this.fetchDashboard();
      if (this.$refs.companiesTable) this.$refs.companiesTable.refresh();
    },

    confirmDeleteInventory(company) {
      this.$bvModal.msgBoxConfirm(`Delete ALL inventory for ${company.name}?`, {
        title: 'Delete Inventory',
        okVariant: 'danger',
        okTitle: 'Delete',
        centered: true,
      }).then(confirmed => {
        if (confirmed) {
          axios.post('/api/super/delete-company-inventory', { company_id: company.id })
            .then(() => this.$toasts.push({ message: 'Inventory deleted', type: 'success' }));
        }
      });
    },

    markAsTest(company) {
      this.$bvModal.msgBoxConfirm(`Mark/unmark ${company.name} as test?`, {
        title: 'Mark as Test',
        okVariant: 'warning',
        okTitle: 'Confirm',
        centered: true,
      }).then(confirmed => {
        if (confirmed) {
          axios.put('/api/company', {
            company_id:  company.id,
            isDashboard: true,
            is_test:     company.is_test === 0 ? 1 : 0,
          }).then(() => {
            this.$toasts.push({ message: 'Company updated', type: 'success' });
            this.fetchDashboard();
            if (this.$refs.companiesTable) this.$refs.companiesTable.refresh();
          });
        }
      });
    },

    // -----------------------------------------------------------------------
    // Global search
    // -----------------------------------------------------------------------
    onGlobalSearchInput() {
      clearTimeout(this.globalSearch.debounceTimer);
      if (!this.globalSearch.query.trim()) {
        this.globalSearch.results = [];
        this.globalSearch.open = false;
        return;
      }
      this.globalSearch.open    = true;
      this.globalSearch.loading = true;
      this.globalSearch.debounceTimer = setTimeout(async () => {
        try {
          const { data } = await axios.get('/api/admin/companies', {
            params: { search: this.globalSearch.query.trim(), per_page: 8 },
          });
          this.globalSearch.results = data.data || [];
        } catch (e) {
          this.globalSearch.results = [];
        } finally {
          this.globalSearch.loading = false;
        }
      }, 300);
    },

    selectSearchResult(company) {
      this.closeSearch();
      this.openDrawer(company);
    },

    closeSearch() {
      this.globalSearch.open = false;
    },

    searchInitials(name) {
      if (!name) return '?';
      const p = name.trim().split(' ');
      return p.length > 1 ? (p[0][0] + p[1][0]).toUpperCase() : p[0][0].toUpperCase();
    },

    searchAvatarColor(name) {
      const palette = ['#E53935','#D81B60','#8E24AA','#5E35B1','#1E88E5','#00897B','#43A047','#F4511E','#FB8C00','#039BE5','#3949AB','#00ACC1','#7CB342','#6D4C41'];
      if (!name) return palette[0];
      let h = 0;
      for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
      return palette[Math.abs(h) % palette.length];
    },

    searchStatusLabel(company) {
      const days = moment().diff(moment(company.created_at), 'days');
      if (days <= 30) return 'Trial';
      if (company.billing && moment(company.billing.due_date).isAfter(moment())) return 'Active';
      return 'Inactive';
    },

    searchStatusBadge(company) {
      const l = this.searchStatusLabel(company);
      return { Active: 'pill-primary', Trial: 'pill-dark', Inactive: 'pill-secondary' }[l];
    },

    // -----------------------------------------------------------------------
    // Extend Trial
    // -----------------------------------------------------------------------
    openExtendTrialModal(company) {
      this.actionTarget        = company;
      this.extendTrialForm.days   = 30;
      this.extendTrialForm.saving = false;
      this.$refs.extendTrialModal.show();
    },

    async submitExtendTrial() {
      this.extendTrialForm.saving = true;
      try {
        const { data } = await axios.post(`/api/admin/companies/${this.actionTarget.id}/extend-trial`, {
          days: this.extendTrialForm.days,
        });
        this.$refs.extendTrialModal.hide();
        this.$toasts.push({ message: data.message, type: 'success' });
        this.refreshAfterAction();
      } catch (e) {
        this.$toasts.push({ message: e.response?.data?.message || 'Failed', type: 'error' });
      } finally {
        this.extendTrialForm.saving = false;
      }
    },

    // -----------------------------------------------------------------------
    // Waive Overdue
    // -----------------------------------------------------------------------
    confirmWaiveOverdue(company) {
      this.$bvModal.msgBoxConfirm(
        `Waive overdue status for ${company.name}? This will push their billing 30 days from today.`,
        { title: 'Waive Overdue', okVariant: 'warning', okTitle: 'Waive', centered: true }
      ).then(ok => {
        if (!ok) return;
        axios.post(`/api/admin/companies/${company.id}/waive-overdue`)
          .then(r => {
            this.$toasts.push({ message: r.data.message, type: 'success' });
            this.refreshAfterAction();
          });
      });
    },

    // -----------------------------------------------------------------------
    // Convert Trial → Active
    // -----------------------------------------------------------------------
    confirmConvertTrial(company) {
      this.$bvModal.msgBoxConfirm(
        `Convert ${company.name} from trial to active? Their account age will be backdated past the 30-day trial window.`,
        { title: 'Convert Trial', okVariant: 'primary', okTitle: 'Convert', centered: true }
      ).then(ok => {
        if (!ok) return;
        axios.post(`/api/admin/companies/${company.id}/convert-trial`)
          .then(r => {
            this.$toasts.push({ message: r.data.message, type: 'success' });
            this.refreshAfterAction();
          });
      });
    },

    // -----------------------------------------------------------------------
    // Reactivate
    // -----------------------------------------------------------------------
    confirmReactivate(company) {
      this.$bvModal.msgBoxConfirm(
        `Reactivate ${company.name}? This will restore the company and its users from archive/suspension.`,
        { title: 'Reactivate Company', okVariant: 'success', okTitle: 'Reactivate', centered: true }
      ).then(ok => {
        if (!ok) return;
        axios.post(`/api/admin/companies/${company.id}/reactivate`)
          .then(r => {
            this.$toasts.push({ message: r.data.message, type: 'success' });
            this.refreshAfterAction();
          });
      });
    },

    // -----------------------------------------------------------------------
    // Archive
    // -----------------------------------------------------------------------
    confirmArchive(company) {
      this.$bvModal.msgBoxConfirm(
        `Archive ${company.name}? The company will be hidden but all data is retained. You can reactivate later.`,
        { title: 'Archive Company', okVariant: 'warning', okTitle: 'Archive', centered: true }
      ).then(ok => {
        if (!ok) return;
        axios.post(`/api/admin/companies/${company.id}/archive`)
          .then(r => {
            this.$toasts.push({ message: r.data.message, type: 'success' });
            this.drawerVisible = false;
            this.refreshAfterAction();
          });
      });
    },

    // -----------------------------------------------------------------------
    // Full Delete
    // -----------------------------------------------------------------------
    confirmDeleteCompany(company) {
      this.$bvModal.msgBoxConfirm(
        `PERMANENTLY DELETE ${company.name}? This cannot be undone. All users, orders, products, billing and data will be destroyed.`,
        { title: 'Delete Company Forever', okVariant: 'danger', okTitle: 'DELETE FOREVER', centered: true }
      ).then(ok => {
        if (!ok) return;
        axios.delete(`/api/admin/companies/${company.id}`)
          .then(r => {
            this.$toasts.push({ message: r.data.message, type: 'success' });
            this.drawerVisible = false;
            this.refreshAfterAction();
          })
          .catch(e => this.$toasts.push({ message: e.response?.data?.message || 'Failed', type: 'error' }));
      });
    },

    // -----------------------------------------------------------------------
    // Force Logout
    // -----------------------------------------------------------------------
    confirmForceLogout(company) {
      this.$bvModal.msgBoxConfirm(
        `Force logout ALL users at ${company.name}? All active sessions and tokens will be revoked immediately.`,
        { title: 'Force Logout All Sessions', okVariant: 'danger', okTitle: 'Force Logout', centered: true }
      ).then(ok => {
        if (!ok) return;
        axios.post(`/api/admin/companies/${company.id}/force-logout`)
          .then(r => this.$toasts.push({ message: r.data.message, type: 'success' }))
          .catch(() => this.$toasts.push({ message: 'Failed to revoke sessions', type: 'error' }));
      });
    },

    // -----------------------------------------------------------------------
    // Password Reset
    // -----------------------------------------------------------------------
    confirmPasswordReset(company) {
      this.$bvModal.msgBoxConfirm(
        `Send a password reset email to the owner of ${company.name}?`,
        { title: 'Send Password Reset', okVariant: 'primary', okTitle: 'Send', centered: true }
      ).then(ok => {
        if (!ok) return;
        axios.post(`/api/admin/companies/${company.id}/password-reset`)
          .then(r => this.$toasts.push({ message: r.data.message, type: 'success' }))
          .catch(e => this.$toasts.push({ message: e.response?.data?.message || 'Failed', type: 'error' }));
      });
    },

    // -----------------------------------------------------------------------
    // Send Notification
    // -----------------------------------------------------------------------
    openNotifyModal(company) {
      this.actionTarget      = company;
      this.notifyForm.title   = '';
      this.notifyForm.message = '';
      this.notifyForm.saving  = false;
      this.$refs.notifyModal.show();
    },

    async submitNotify() {
      this.notifyForm.saving = true;
      try {
        const { data } = await axios.post(`/api/admin/companies/${this.actionTarget.id}/notify`, {
          title:   this.notifyForm.title || undefined,
          message: this.notifyForm.message,
        });
        this.$refs.notifyModal.hide();
        this.$toasts.push({ message: data.message, type: 'success' });
      } catch (e) {
        this.$toasts.push({ message: e.response?.data?.message || 'Failed', type: 'error' });
      } finally {
        this.notifyForm.saving = false;
      }
    },

    // -----------------------------------------------------------------------
    // Clipboard copy feedback
    // -----------------------------------------------------------------------
    onEmailCopied(email) {
      this.$toasts.push({ message: `Copied: ${email}`, type: 'success' });
    },

    // -----------------------------------------------------------------------
    // Shared post-action refresh helper
    // -----------------------------------------------------------------------
    refreshAfterAction() {
      this.fetchDashboard();
      if (this.$refs.companiesTable) this.$refs.companiesTable.refresh();
    },

    formatDateShort(date) {
      return moment(date).format('D MMM YY');
    },

    openCompanyFromChurn(risk) {
      // Risk object has id + name; fetch full company from table data or open drawer directly
      this.selectedCompany = { id: risk.id, name: risk.name };
      this.drawerVisible = true;
    },
  },
};
</script>

<style scoped>
.tab-pane-wrapper {
  min-height: 400px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 14px;
  padding: 20px;
}
.anim-spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.search-result-item:hover {
  background: #f8fafc;
}

/* Strip the date-range-picker library's own boxed toggle so only our
   .pill-input styling shows — otherwise it nests our pill inside its
   default .form-control.reportrange-text box (double border/padding). */
.header-date-picker ::v-deep .reportrange-text {
  background: transparent !important;
  border: none !important;
  padding: 0 !important;
  height: auto !important;
  width: auto !important;
  min-width: 0 !important;
  display: inline-flex !important;
}

/* Pill search / date input */
.pill-input {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 12px;
  background: #fff;
  font-size: 13px;
  color: #0f172a;
}
.pill-input input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  color: #0f172a;
  flex-grow: 1;
  min-width: 0;
}

/* Segmented pill tab bar */
.pill-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 6px;
  background: #fff;
}
.pill-tab {
  display: inline-flex;
  align-items: center;
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  white-space: nowrap;
  text-decoration: none;
}
.pill-tab:hover {
  background: #f8fafc;
  color: #0f172a;
  text-decoration: none;
}
.pill-tab.active {
  background: #0f172a;
  color: #fff;
}

/* Generic status pill (search results, etc) */
.pill {
  display: inline-block;
  font-size: .7rem;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 20px;
  background: #f1f5f9;
  color: #0f172a;
}
.pill-primary { background: rgba(13,110,253,.1); color: #0d6efd; }
.pill-dark { background: #f1f5f9; color: #0f172a; }
.pill-secondary { background: #f1f5f9; color: #64748b; }
</style>
